import { SanityIcon } from "@/cms/icons/Icon";
import classNames from "classnames";
import { type FC, Fragment, type ReactNode, type JSX } from "react";
import { getPageLinkFromLinkConfig } from "../selectors";
import { getTrackingDataset } from "@/tracking/utils";
import { ContentData } from "@/cms";
import { Link as LinkType } from "../types";
import { LinkQueryResult } from "@/cms/sanity.types";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";
import { UITrackData } from "@/tracking/ui-event";
import { Button, Link } from "@cappelendamm/design-system/react";
import { theme } from "@/theming/themed-stylesheet";

/**
 * @deprecated
 * Should instead load data using linkQueryResult
 */
export const mapLinkConfigToQueryResult = (
  config: LinkType,
): NonNullable<LinkQueryResult> =>
  config as unknown as NonNullable<LinkQueryResult>;

export type CMSLinkVariants = "menu" | NonNullable<LinkQueryResult>["variant"];

export type CMSLinkProps = ContentData<
  Omit<NonNullable<LinkQueryResult>, "variant">
> &
  JSX.IntrinsicElements["a"] & {
    data: {
      variant?: CMSLinkVariants;
    };
  };

function LinkComponent({
  variant,
  children,
  ...rest
}: Omit<NextLinkProps, "as"> & {
  variant: CMSLinkVariants;
  children: ReactNode;
} & JSX.IntrinsicElements["a"]) {
  if (variant === "button")
    return (
      <Button as={NextLink} {...rest}>
        {children}
      </Button>
    );
  return (
    <Link
      as={NextLink}
      variant={variant === "menu" ? "menu" : undefined}
      {...rest}
    >
      {children}
    </Link>
  );
}

export const CMSLink: FC<CMSLinkProps> = ({
  data: {
    variant,
    stylesheet,
    cartItems,
    contentAlignment,
    icon,
    title,
    microcopy,
    ...link
  },
  children,
  ...rest
}) => {
  const href = getPageLinkFromLinkConfig({
    link: { ...link, cartItems },
  });
  const tracking = getTrackingDataset<UITrackData>({
    ui: {
      item_type: variant ?? "",
      item_id: link.slug ?? "unknown-link-id",
      item_name: "link",
    },
  });

  const Wrapper = microcopy ? "div" : Fragment;

  return (
    <Wrapper>
      <LinkComponent
        {...tracking}
        {...rest}
        variant={variant ?? "link"}
        href={href}
        style={theme(stylesheet)}
      >
        <div
          className={classNames("flex gap-2", {
            "flex-row-reverse justify-end": contentAlignment === "left",
          })}
        >
          {title && microcopy ? `${title} *` : title}
          {icon && <SanityIcon data={{ ...icon }} size={24} />}
        </div>
        {children}
      </LinkComponent>
      {microcopy ? <p>{`* ${microcopy}`}</p> : null}
    </Wrapper>
  );
};

export default CMSLink;
